import axiosInstance from './axiosInstance';

const REPORTS_API_BASE = 'techsavanna_pos.api.reports';
const CASHIER_API_BASE = 'techsavanna_pos.api.cashier_reports_api';
const PRODUCT_API_BASE = 'techsavanna_pos.api.product_api';

// Helper to build endpoint URL
// Note: The baseURL in axiosInstance already includes /api/method/, so we just return the endpoint string
const buildEndpoint = (endpoint) =>
  `${REPORTS_API_BASE}.${endpoint}`;

// Helper to extract response data
const extractResponseData = (response) => {
  // New API structure: { success: true, data: {...} }
  if (response.data?.success && 'data' in (response.data ?? {})) {
    return response.data.data;
  }
  // Frappe-wrapped structure: { message: { success: true, data: {...} } }
  if (response.data?.message?.success && 'data' in (response.data?.message ?? {})) {
    return response.data.message.data;
  }
  if (response.data?.message && typeof response.data.message === 'object') {
    return response.data.message;
  }
  return response.data?.message || response.data;
};

// Helper to extract error message
const extractErrorMessage = (error) => {
  if (error.response?.data) {
    const errorData = error.response.data;
    
    // Check for _error_message first (most direct error message)
    if (errorData._error_message) {
      return errorData._error_message;
    }
    
    // Parse _server_messages to extract detailed error messages
    if (errorData._server_messages) {
      try {
        const messages = JSON.parse(errorData._server_messages);
        if (Array.isArray(messages) && messages.length > 0) {
          const firstMessage = typeof messages[0] === 'string' 
            ? JSON.parse(messages[0]) 
            : messages[0];
          if (firstMessage?.message) {
            // Remove HTML tags from message if present
            const cleanMessage = firstMessage.message.replace(/<[^>]*>/g, '');
            return cleanMessage;
          }
        }
      } catch (e) {
        // If parsing fails, continue to other error sources
      }
    }
    
    // Check for nested message structure
    if (errorData.message) {
      if (typeof errorData.message === 'string') {
        return errorData.message;
      }
      if (typeof errorData.message === 'object' && errorData.message.message) {
        return errorData.message.message;
      }
    }
    
  }

  return 'An error occurred';
};

// ============================================================================
// ITEM SEARCH (used by ItemAutocomplete in report filters)
// ============================================================================

/**
 * Search items by name or code for autocomplete dropdowns.
 * @param {string} searchTerm - Free-text search against item_code and item_name
 * @param {string} [company] - Company filter (optional)
 * @param {number} [pageSize=20] - Max results
 * @returns {Promise<Array<{item_code: string, item_name: string}>>}
 */
export const searchItems = async (searchTerm = '', company = null, pageSize = 20, signal = null) => {
  try {
    const response = await axiosInstance.post(
      `${PRODUCT_API_BASE}.get_products`,
      {
        search_term: searchTerm,
        ...(company ? { company } : {}),
        page: 1,
        page_size: pageSize,
        disabled: false,
      },
      signal ? { signal } : undefined
    );
    // Frappe wraps return values in response.data.message
    const msg = response.data?.message;
    if (msg && Array.isArray(msg.products)) return msg.products;
    if (Array.isArray(msg)) return msg;
    if (Array.isArray(response.data?.products)) return response.data.products;
    return [];
  } catch (err) {
    // Ignore AbortError (request was intentionally cancelled)
    if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return [];
    return [];
  }
};

// ============================================================================
// SALES ANALYTICS REPORTS
// ============================================================================

/**
 * Fetch Sales Analytics Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date (YYYY-MM-DD, optional)
 * @param {string} params.end_date - End date (YYYY-MM-DD, optional)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.item_group - Item group filter (optional)
 * @param {string} params.customer - Customer filter (optional)
 * @param {string} params.staff - Staff email (owner) filter (optional)
 * @param {string} params.group_by - Group by: date, item_group, or customer (optional, default: date)
 * @returns {Promise<Object>} Report data
 */
export const fetchSalesAnalyticsReport = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('sales_analytics_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Export Sales Analytics Report
 * @param {Object} params - Report parameters (same as fetchSalesAnalyticsReport)
 * @param {string} params.format - Export format: csv, excel, or pdf (default: csv)
 * @returns {Promise<Blob>} File blob for download
 */
export const exportSalesAnalyticsReport = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('export_sales_analytics_report'),
      params
    );
    const result = extractResponseData(response);
    if (!result?.data) throw new Error('No export data returned');
    const byteStr = atob(result.data);
    const bytes = new Uint8Array(byteStr.length);
    for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
    const blob = new Blob([bytes], { type: result.content_type || 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename || `sales_analytics_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return result;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// ============================================================================
// SALES ANALYTICS REPORTS
// ============================================================================

/**
 * Fetch Product Sales Analytics Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date (YYYY-MM-DD, optional)
 * @param {string} params.end_date - End date (YYYY-MM-DD, optional)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.item_group - Item group filter (optional)
 * @param {string} params.product - Specific product filter (optional)
 * @param {string} params.customer - Customer filter (optional)
 * @param {string} params.payment_method - Payment method filter (optional)
 * @param {string} params.staff - Staff email (owner) filter (optional)
 * @returns {Promise<Object>} Report data
 */
export const fetchProductSalesAnalyticsReport = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('product_sales_analytics_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// ============================================================================
// SALES PERFORMANCE REPORTS
// ============================================================================

/**
 * Fetch Sales by Staff Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.from_date - Start date (YYYY-MM-DD, optional)
 * @param {string} params.to_date - End date (YYYY-MM-DD, optional)
 * @param {string} params.from_time - Start time (HH:MM, optional)
 * @param {string} params.to_time - End time (HH:MM, optional)
 * @returns {Promise<Object>} Report data with { data, summary, period }
 */
export const fetchSalesByStaff = async (params) => {
  try {
    // One row per staff member, searched and exported client-side — ask for
    // all of them. The endpoint used to default to 10 rows, which silently
    // dropped every staff member outside the top 10 sellers from the report.
    const response = await axiosInstance.post(
      buildEndpoint('sales_by_staff_report'),
      { page: 1, page_size: 1000, ...params }
    );
    // The endpoint returns { success, data:[...], summary:{...}, period:{...} }.
    // Frappe may wrap this inside response.data.message, so unwrap to the
    // object that carries the `data` array (keeping summary/period intact).
    const payload =
      (response.data?.message && typeof response.data.message === 'object')
        ? response.data.message
        : response.data;
    return payload || {};
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Waiter Revenue Report
 * Total sales/revenue generated by each waiter, with a per-warehouse breakdown.
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.from_date - Start date (YYYY-MM-DD, optional)
 * @param {string} params.to_date - End date (YYYY-MM-DD, optional)
 * @param {string} params.from_time - Start time (HH:MM, optional)
 * @param {string} params.to_time - End time (HH:MM, optional)
 * @returns {Promise<Object>} { data, warehouse_breakdown, summary, period }
 */
export const fetchWaiterRevenue = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('waiter_revenue_report'),
      params
    );
    const payload =
      (response.data?.message && typeof response.data.message === 'object')
        ? response.data.message
        : response.data;
    return payload || {};
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Value Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (optional)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @returns {Promise<Object>} Value data
 */
export const fetchInventoryValueReport = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_value_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Value by Category Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.item_group - Item group filter (optional)
 * @returns {Promise<Array>} Report data array
 */
export const fetchInventoryValueByCategory = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_value_by_category_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Cost Method Comparison Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {Array<string>} params.cost_methods - Array of cost methods: ["FIFO", "LIFO", "Weighted Average"] (optional)
 * @returns {Promise<Object>} Comparison data
 */
export const fetchCostMethodComparison = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_cost_method_comparison_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Value Trends Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date (YYYY-MM-DD, required)
 * @param {string} params.end_date - End date (YYYY-MM-DD, required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.period - Period grouping: daily, weekly, or monthly (optional, default: daily)
 * @returns {Promise<Array>} Trends data array
 */
export const fetchInventoryValueTrends = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_value_trends_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// ============================================================================
// STOCK MOVEMENT ANALYSIS REPORTS
// ============================================================================

/**
 * Fetch Inventory Movement Report
 * @param {Object} params - Report parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD, required)
 * @param {string} params.end_date - End date (YYYY-MM-DD, required)
 * @param {string} params.company - Company name (optional)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @returns {Promise<Object>} Movement data
 */
export const fetchInventoryMovementReport = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_movement_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Turnover Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date (YYYY-MM-DD, required)
 * @param {string} params.end_date - End date (YYYY-MM-DD, required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.item_group - Item group filter (optional)
 * @returns {Promise<Array>} Turnover data array
 */
export const fetchInventoryTurnover = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_turnover_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Days on Hand Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.item_group - Item group filter (optional)
 * @param {number} params.period_days - Days to calculate average consumption (optional, default: 30)
 * @returns {Promise<Array>} Days on hand data array
 */
export const fetchDaysOnHand = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_days_on_hand_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const exportDaysOnHandReport = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('export_days_on_hand_report'),
      params
    );
    const result = extractResponseData(response);
    if (!result?.data) throw new Error('No export data returned');
    const byteStr = atob(result.data);
    const bytes = new Uint8Array(byteStr.length);
    for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
    const blob = new Blob([bytes], { type: result.content_type || 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename || `days_on_hand_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return result;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Movement Patterns Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date (YYYY-MM-DD, required)
 * @param {string} params.end_date - End date (YYYY-MM-DD, required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.analysis_type - Analysis type: seasonal, trend, or forecast (optional, default: trend)
 * @returns {Promise<Object>} Movement patterns data
 */
export const fetchMovementPatterns = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_movement_patterns_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// ============================================================================
// AGING STOCK REPORTS
// ============================================================================

/**
 * Fetch Stock Aging Report (Enhanced)
 * @param {Object} params - Report parameters
 * @param {number} params.slow_moving_threshold - Threshold for slow moving items (optional)
 * @returns {Promise<Object>} Stock aging data by brackets
 */
export const fetchStockAging = async (params = {}) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('stock_aging_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Obsolescence Risk Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.risk_level - Filter by risk level: low, medium, or high (optional, default: all)
 * @returns {Promise<Array>} Obsolescence risk data array
 */
export const fetchObsolescenceRisk = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_obsolescence_risk_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Aging Recommendations Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @returns {Promise<Array>} Aging recommendations data array
 */
export const fetchAgingRecommendations = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_aging_recommendations_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// ============================================================================
// PERFORMANCE METRICS REPORTS
// ============================================================================

/**
 * Fetch Inventory Accuracy Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date for stock counts (YYYY-MM-DD, optional)
 * @param {string} params.end_date - End date for stock counts (YYYY-MM-DD, optional)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @returns {Promise<Object>} Accuracy metrics data
 */
export const fetchInventoryAccuracy = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_accuracy_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Variance Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date for stock counts (YYYY-MM-DD, optional)
 * @param {string} params.end_date - End date for stock counts (YYYY-MM-DD, optional)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {number} params.variance_threshold - Minimum variance value to include (optional)
 * @returns {Promise<Array>} Variance data array
 */
export const fetchInventoryVariance = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_variance_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Adjustment Trends Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date (YYYY-MM-DD, required)
 * @param {string} params.end_date - End date (YYYY-MM-DD, required)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.adjustment_type - Filter by: increase, decrease, or all (optional, default: all)
 * @returns {Promise<Array>} Adjustment trends data array
 */
export const fetchAdjustmentTrends = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_adjustment_trends_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Inventory Transfer Efficiency Report
 * @param {Object} params - Report parameters
 * @param {string} params.company - Company name (required)
 * @param {string} params.start_date - Start date (YYYY-MM-DD, required)
 * @param {string} params.end_date - End date (YYYY-MM-DD, required)
 * @param {string} params.from_warehouse - Source warehouse filter (optional)
 * @param {string} params.to_warehouse - Destination warehouse filter (optional)
 * @returns {Promise<Object>} Transfer efficiency metrics data
 */
export const fetchTransferEfficiency = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_transfer_efficiency_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// ============================================================================
// INVENTORY SUMMARY REPORT
// ============================================================================

/**
 * Fetch Inventory Summary Report
 * @param {Object} params - Report parameters (optional, endpoint accepts no parameters)
 * @returns {Promise<Array>} Report data array with { warehouse, total_qty, total_value }
 */
export const fetchInventorySummary = async (params = {}) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('inventory_summary_report'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// ============================================================================
// GRN (GOODS RECEIPT NOTE) REPORTS
// ============================================================================

/**
 * Fetch GRN List Report
 * @param {Object} params - Query parameters
 * @param {string} params.company - Company name (optional, defaults to user's default)
 * @param {string} params.supplier - Supplier name filter (optional)
 * @param {string} params.purchase_order - Purchase Order number filter (optional)
 * @param {string} params.warehouse - Warehouse name filter (optional)
 * @param {string} params.start_date - Start date filter (YYYY-MM-DD, optional)
 * @param {string} params.end_date - End date filter (YYYY-MM-DD, optional)
 * @param {string} params.status - GRN status filter (optional, e.g., "Received", "To Bill", "Completed")
 * @param {number} params.docstatus - Document status (optional, 0=Draft, 1=Submitted, 2=Cancelled)
 * @param {number} params.page - Page number for pagination (optional, default: 1)
 * @param {number} params.page_size - Number of records per page (optional, default: 20, max: 100)
 * @returns {Promise<Object>} Response object with { success: boolean, data: GRN[], meta: PaginationMeta }
 */
export const fetchGRNList = async (params = {}) => {
  try {
    // Validate page_size doesn't exceed maximum
    if (params.page_size && params.page_size > 100) {
      throw new Error('Page size cannot exceed 100');
    }

    // Validate date format if provided
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (params.start_date && !dateRegex.test(params.start_date)) {
      throw new Error('Invalid start_date format. Expected YYYY-MM-DD');
    }
    if (params.end_date && !dateRegex.test(params.end_date)) {
      throw new Error('Invalid end_date format. Expected YYYY-MM-DD');
    }

    const response = await axiosInstance.get(
      buildEndpoint('grn_list_report'),
      { params }
    );

    // Extract response data - handle both direct response and nested structures
    const data = extractResponseData(response);
    
    // If the response already has success/data/meta structure, return as is
    if (data?.success !== undefined) {
      return data;
    }
    
    // Otherwise, wrap in standard structure for consistency
    return {
      success: true,
      data: Array.isArray(data) ? data : (data?.data || []),
      meta: data?.meta || {
        page: params.page || 1,
        page_size: params.page_size || 20,
        total: Array.isArray(data) ? data.length : (data?.total || 0),
        total_pages: Math.ceil(
          (Array.isArray(data) ? data.length : (data?.total || 0)) / (params.page_size || 20)
        ),
      },
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch GRN Detail Report
 * @param {Object} params - Query parameters
 * @param {string} params.grn_no - GRN number (Purchase Receipt name) (required)
 * @returns {Promise<Object>} Response object with { success: boolean, data: GRNDetail }
 */
export const fetchGRNDetails = async (params) => {
  try {
    // Validate required parameter
    if (!params || !params.grn_no) {
      throw new Error('GRN number (grn_no) is required');
    }

    const response = await axiosInstance.get(
      buildEndpoint('grn_detail_report'),
      { params: { grn_no: params.grn_no } }
    );

    // Extract response data
    const data = extractResponseData(response);
    
    // If the response already has success/data structure, return as is
    if (data?.success !== undefined) {
      return data;
    }
    
    // Otherwise, wrap in standard structure for consistency
    return {
      success: true,
      data: data || {},
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

// ============================================================================
// CUSTOMER & SUPPLIER REPORTS
// ============================================================================

/**
 * Fetch Customer Report
 * @param {Object} params - Request parameters
 * @param {string} params.customer - Customer name/ID (required)
 * @param {string} params.from_date - Start date YYYY-MM-DD (optional)
 * @param {string} params.to_date - End date YYYY-MM-DD (optional)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.company - Company name (optional)
 * @returns {Promise<Object>} Report data
 */
export const fetchCustomerReport = async (params) => {
  try {
    if (!params?.customer) {
      throw new Error('customer is required');
    }
    const response = await axiosInstance.get(
      buildEndpoint('get_customer_report'),
      { params }
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Supplier Report
 * @param {Object} params - Request parameters
 * @param {string} params.supplier - Supplier name/ID (required)
 * @param {string} params.from_date - Start date YYYY-MM-DD (optional)
 * @param {string} params.to_date - End date YYYY-MM-DD (optional)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.company - Company name (optional)
 * @returns {Promise<Object>} Report data
 */
export const fetchSupplierReport = async (params) => {
  try {
    if (!params?.supplier) {
      throw new Error('supplier is required');
    }
    const response = await axiosInstance.get(
      buildEndpoint('get_supplier_report'),
      { params }
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};


// ============================================================================
// ACCOUNTS REPORTS
// ============================================================================

/**
 * Fetch list of POS Opening Entries for Z-report dropdown
 * @param {Object} params - Request parameters
 * @param {string} params.company - Company filter (optional)
 * @returns {Promise<Array>} Array of POS Opening Entries
 */
export const fetchPOSOpeningEntries = async (params = {}) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('get_pos_opening_entries'),
      params
    );
    const result = extractResponseData(response);
    return Array.isArray(result) ? result : (result?.data || result || []);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Z-Report (End of Day Summary).
 *
 * Single-session mode: pass { pos_opening_entry }
 * Company-wide mode:   pass { company } plus optional { pos_profile, user, from_date, to_date }
 *
 * @param {Object} params
 * @returns {Promise<Object>} Z report response data
 */
export const fetchZReport = async (params) => {
  try {
    if (!params?.pos_opening_entry && !params?.company) {
      throw new Error('Provide either pos_opening_entry or company.');
    }

    const body = {};
    if (params.pos_opening_entry) body.pos_opening_entry = params.pos_opening_entry;
    if (params.company)           body.company           = params.company;
    if (params.pos_profile)       body.pos_profile       = params.pos_profile;
    if (params.user)              body.user              = params.user;
    if (params.warehouse)         body.warehouse         = params.warehouse;
    if (params.from_date)         body.from_date         = params.from_date;
    if (params.to_date)           body.to_date           = params.to_date;
    if (params.from_time)         body.from_time         = params.from_time;
    if (params.to_time)           body.to_time           = params.to_time;
    if (params.product)           body.product           = params.product;
    if (params.payment_method)    body.payment_method    = params.payment_method;

    const response = await axiosInstance.post(buildEndpoint('get_z_report'), body);
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch the combined Customer Statement — one row per customer with total
 * orders, total amount, paid amount and balance due for a date range.
 *
 * @param {Object} params
 * @param {string} params.company - Company name (required)
 * @param {string} [params.from_date]
 * @param {string} [params.to_date]
 * @param {string} [params.warehouse]
 * @returns {Promise<Object>} Customer statement response data
 */
export const fetchCustomerStatementReport = async (params) => {
  try {
    if (!params?.company) {
      throw new Error('company is required');
    }

    const body = { company: params.company };
    if (params.from_date) body.from_date = params.from_date;
    if (params.to_date) body.to_date = params.to_date;
    if (params.warehouse) body.warehouse = params.warehouse;

    const response = await axiosInstance.post(buildEndpoint('get_customer_statement_report'), body);
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch the per-customer detail statement — one row per order (invoice)
 * for a single customer in a date/time range, with a Fully Paid /
 * Partially Paid / Pending breakdown. This is the printable drill-down
 * of the combined Customer Statement for one customer.
 *
 * @param {Object} params
 * @param {string} params.customer - Customer name/ID (required)
 * @param {string} [params.company]
 * @param {string} [params.from_date]
 * @param {string} [params.to_date]
 * @param {string} [params.from_time] - HH:MM:SS
 * @param {string} [params.to_time] - HH:MM:SS
 * @returns {Promise<Object>} Customer statement detail response data
 */
export const fetchCustomerStatementDetailReport = async (params) => {
  try {
    if (!params?.customer) {
      throw new Error('customer is required');
    }

    const body = { customer: params.customer };
    if (params.company) body.company = params.company;
    if (params.from_date) body.from_date = params.from_date;
    if (params.to_date) body.to_date = params.to_date;
    if (params.from_time) body.from_time = params.from_time;
    if (params.to_time) body.to_time = params.to_time;

    const response = await axiosInstance.post(buildEndpoint('get_customer_statement_detail_report'), body);
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch Profit and Loss statement
 * @param {Object} params - Request parameters
 * @param {string} params.company - Company name (required)
 * @returns {Promise<Object>} P&L response payload
 */
export const fetchProfitAndLoss = async (params) => {
  try {
    if (!params?.company) {
      throw new Error('company is required');
    }

    const response = await axiosInstance.post(
      buildEndpoint('get_profit_and_loss'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Fetch per-product P&L (revenue, COGS, gross profit, margin)
 * @param {Object} params
 * @param {string} params.company - Company name (required)
 * @param {string} params.from_date - Start date (YYYY-MM-DD)
 * @param {string} params.to_date - End date (YYYY-MM-DD)
 * @param {string} params.warehouse - Warehouse filter (optional)
 * @param {string} params.item_group - Item group filter (optional)
 * @param {number} params.limit - Max rows (default 100)
 * @returns {Promise<Object>} { success, data: [{item_code, item_name, item_group, qty_sold, revenue, cogs, gross_profit, margin_pct}] }
 */
export const fetchProductPnl = async (params) => {
  try {
    const response = await axiosInstance.post(
      buildEndpoint('get_product_pnl'),
      params
    );
    return extractResponseData(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const buildCashierEndpoint = (endpoint) =>
  `${CASHIER_API_BASE}.${endpoint}`;

// Cashier report pages expect the full report object ({ data, summary }),
// not just the unwrapped rows that extractResponseData returns.
const fetchCashierReport = async (endpoint, params = {}) => {
  try {
    const response = await axiosInstance.post(buildCashierEndpoint(endpoint), params);
    const payload = response.data?.message || response.data?.data || response.data;
    return {
      data: payload.data || [],
      summary: payload.summary || null,
      message: payload.message || '',
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const fetchPaymentMethodReport = (params = {}) =>
  fetchCashierReport('payment_method_report', params);

export const fetchMpesaNotifications = (params = {}) =>
  fetchCashierReport('mpesa_notifications', params);

export const fetchSubmittedPayments = (params = {}) =>
  fetchCashierReport('submitted_payments', params);

export const fetchTotalSalesPerPerson = async (params = {}) => {
  const response = await axiosInstance.post(
    buildCashierEndpoint('total_sales_per_person'),
    params
  );
  const payload = response.data?.message || response.data?.data || response.data;
  return {
    data:              payload.data              || [],
    warehouse_columns: payload.warehouse_columns || [],
    meta:              payload.meta              || null,
    summary:           payload.summary           || null,
    message:           payload.message           || '',
  };
};

export const fetchSalesDetailReport = async (params = {}) => {
  const response = await axiosInstance.post(
    buildCashierEndpoint('sales_detail_report'),
    params
  );
  const payload = response.data?.message || response.data?.data || response.data;
  return {
    data:    payload.data    || [],
    meta:    payload.meta    || null,
    summary: payload.summary || null,
    message: payload.message || '',
  };
};

export const fetchItemSalesReport = async (params = {}) => {
  const response = await axiosInstance.post(
    buildCashierEndpoint('item_sales_report'),
    params
  );
  const payload = response.data?.message || response.data?.data || response.data;
  return {
    data:              payload.data              || [],
    warehouses:        payload.warehouses        || [],
    departments:       payload.departments       || [],
    categories:        payload.categories        || [],
    matrix:            payload.matrix            || null,
    meta:              payload.meta              || null,
    summary:           payload.summary           || null,
    wholesale_enabled: payload.wholesale_enabled  || false,
    retail_products:   payload.retail_products   || [],
    wholesale_products: payload.wholesale_products || [],
    message:           payload.message           || '',
  };
};

export const fetchTotalSalesPerWarehouse = async (params = {}) => {
  const response = await axiosInstance.post(
    buildCashierEndpoint('total_sales_per_warehouse'),
    params
  );
  const payload = response.data?.message || response.data?.data || response.data;
  return {
    data:       payload.data       || [],
    warehouses: payload.warehouses || [],
    summary:    payload.summary    || null,
    message:    payload.message    || '',
  };
};

// ── Till Numbers ──────────────────────────────────────────────────────────────
const buildWarehouseEndpoint = (fn) =>
  `techsavanna_pos.api.warehouse_api.${fn}`;

export const listTillNumbers = async (company) => {
  const r = await axiosInstance.get(buildWarehouseEndpoint('list_till_numbers'), { params: { company } });
  const p = r.data?.message ?? r.data;
  return Array.isArray(p?.data) ? p.data : [];
};

export const saveTillNumber = async ({ warehouse, till_number, company }) => {
  const r = await axiosInstance.post(buildWarehouseEndpoint('save_till_number'), { warehouse, till_number, company });
  const p = r.data?.message ?? r.data;
  if (p?.success === false) throw new Error(p.message || 'Failed to save till number');
  return p;
};

export const deleteTillNumber = async (warehouse) => {
  const r = await axiosInstance.post(buildWarehouseEndpoint('delete_till_number'), { warehouse });
  const p = r.data?.message ?? r.data;
  if (p?.success === false) throw new Error(p.message || 'Failed to delete till number');
  return p;
};

